import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import RetentionHome from "../components/RetentionHome";
import { TestimonialStrip } from "../components/TestimonialStrip";
import { api } from "../lib/api";
import { ArrowRight, BookOpen, Compass, ChartLineUp, ShieldCheck } from "@phosphor-icons/react";

const HERO_BG =
  "/photos/annuity/annuity-6.png";

const LEON_HERO =
  "/photos/iul/iul-9.png";

const PRINCIPLES = [
  {
    icon: BookOpen,
    eyebrow: "Principle 01",
    title: "Education before recommendation.",
    body: "Strategy beats product. We teach the mechanics, the math, and the trade-offs — so by the time you make a decision, you actually understand what you're choosing.",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Principle 02",
    title: "Contractual over conjectural.",
    body: "Hope is not a strategy. We engineer income, growth, and protection through contracts — written by A-rated carriers — that don't depend on the market doing what we wish.",
  },
  {
    icon: Compass,
    eyebrow: "Principle 03",
    title: "Influence, never pressure.",
    body: "We don't close. We clarify. The strongest decisions come from understanding — and our role is to make complex tools simple enough that the right answer becomes obvious to you.",
  },
];

export default function Landing() {
  const [topics, setTopics] = useState([]);
  useEffect(() => {
    api.get("/education").then((r) => setTopics((r.data.items || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <RetentionHome />

      {/* HERO — education-first */}
      <section className="relative pt-20 pb-24 lg:pt-28 lg:pb-32 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-exodus-bg/70 via-exodus-bg/85 to-exodus-bg" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <p className="eyebrow rise"><span className="gold-divider" />A Private-Wealth Education Practice</p>
            <h1 data-testid="hero-headline" className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] rise rise-1">
              Know where you stand. <span className="gold-text italic">See what could be stronger.</span>
            </h1>
            <p className="text-lg lg:text-xl text-exodus-muted max-w-xl leading-relaxed rise rise-2">
              Start with your free Financial Clarity Score — a transparent 0–100 educational assessment across cash flow, taxes, protection, net worth, and retirement. Prefer a quick strategy match? Find My Fit. Want the math? Run the Numbers. Every path leads to clarity — and a conversation with Leon only when you're ready.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 rise rise-3">
              <Link to="/clarity-score" data-testid="hero-cta-clarity" className="btn-gold flex-1 sm:flex-initial justify-center text-center">
                Get Your Clarity Score
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/quiz" data-testid="hero-cta-quiz" className="btn-ghost flex-1 sm:flex-initial justify-center text-center">
                Start My Fit
              </Link>
              <Link to="/calculator" data-testid="hero-cta-calc" className="btn-ghost flex-1 sm:flex-initial justify-center text-center">
                Run the Numbers
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
            <p className="text-xs text-exodus-muted/70 mt-1">
              Free education. Private sessions reserved with a $100 refundable deposit.
              {" · "}
              <Link to="/reserve" className="underline hover:text-exodus-gold" data-testid="hero-cta-secondary">Book directly</Link>
            </p>

            {/* ── Trust Section (social proof) ── */}
            <div data-testid="hero-trust" className="mt-10 pt-8 border-t border-exodus-border grid sm:grid-cols-3 gap-6 rise rise-4">
              <div>
                <p className="font-display text-3xl gold-text">12+ yrs</p>
                <p className="text-xs text-exodus-muted mt-1 leading-relaxed">
                  Guiding families through IUL, FIA, and whole-life structures across 40+ states.
                </p>
              </div>
              <div>
                <p className="font-display text-3xl gold-text">20,000+</p>
                <p className="text-xs text-exodus-muted mt-1 leading-relaxed">
                  Clients educated — most never knew the strategies existed before meeting Leon.
                </p>
              </div>
              <div>
                <p className="font-display text-3xl gold-text">A-rated only</p>
                <p className="text-xs text-exodus-muted mt-1 leading-relaxed">
                  Every recommendation comes from AM Best A or better carriers. No fine print, no pressure.
                </p>
              </div>
            </div>
            <TestimonialStrip />
          </div>
          <div className="lg:col-span-5 relative rise rise-4">
            <div className="card-surface p-2">
              <img src={LEON_HERO} alt="Leon presenting at EXODUS LIFE & WEALTH" className="w-full block" data-testid="hero-leon-image" />
            </div>
            <div className="absolute -bottom-6 -left-6 glass px-6 py-4">
              <p className="eyebrow">Your Educator</p>
              <p className="font-display text-2xl">Leon</p>
              <p className="text-xs text-exodus-muted">The Structured Advisor</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="eyebrow"><span className="gold-divider" />Our Principles</p>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              How a private wealth education practice should actually work.
            </h2>
          </div>
          <p className="lg:col-span-5 text-exodus-muted leading-relaxed">
            We exist to make complex financial instruments — IULs, annuities, permanent life insurance — finally make sense. Then, only if it's the right fit, we help you implement.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-exodus-border">
          {PRINCIPLES.map((p, i) => (
            <div key={i} data-testid={`principle-${i}`} className="bg-exodus-bg p-10 lg:p-12 hover:bg-exodus-surface transition-colors">
              <p.icon size={36} weight="thin" className="text-exodus-gold mb-8" />
              <p className="eyebrow">{p.eyebrow}</p>
              <h3 className="font-display text-3xl mt-3 mb-5">{p.title}</h3>
              <p className="text-exodus-muted leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EDUCATION LIBRARY PREVIEW */}
      <section className="bg-exodus-surface py-24 lg:py-32 border-y border-exodus-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <p className="eyebrow"><span className="gold-divider" />The Library</p>
              <h2 className="font-display text-4xl lg:text-5xl mt-3 max-w-2xl leading-tight">
                Twelve concepts. Twelve clear answers.
              </h2>
            </div>
            <Link to="/learn" data-testid="library-link-all" className="btn-ghost">
              Browse all
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-exodus-border">
            {topics.map((t) => (
              <Link
                key={t.slug}
                to={`/learn/${t.slug}`}
                data-testid={`topic-preview-${t.slug}`}
                className="group bg-exodus-bg p-8 lg:p-10 hover:bg-exodus-elevated transition-colors block"
              >
                <p className="eyebrow text-xs">{t.category}</p>
                <h3 className="font-display text-2xl mt-3 leading-tight group-hover:text-exodus-gold transition-colors">
                  {t.title}
                </h3>
                <p className="text-sm text-exodus-muted mt-4 leading-relaxed line-clamp-3">{t.summary}</p>
                <div className="flex items-center gap-2 mt-6 text-xs gold-text">
                  Read the breakdown
                  <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <p className="eyebrow"><span className="gold-divider" />How a Session Works</p>
        <h2 className="font-display text-4xl lg:text-5xl mt-4 mb-16 max-w-3xl leading-tight">
          From a curious read to a personal blueprint — at your pace.
        </h2>
        <div className="grid md:grid-cols-4 gap-8">
          {[
            { n: "01", t: "Browse the Library", b: "Read the concepts at your pace. Twelve topics, plain English, no signup required." },
            { n: "02", t: "Reserve a Session", b: "A $100 refundable deposit holds your private 30-minute session with Leon." },
            { n: "03", t: "Walk the Blueprint", b: "We map your current picture, define your goals, and quantify gaps — no product yet." },
            { n: "04", t: "Decide on Your Own Terms", b: "If a strategy fits, we structure it. If not, you leave with clarity. That's the only success metric." },
          ].map((p, i) => (
            <div key={i}>
              <p className="font-mono text-sm gold-text mb-6">{p.n} / 04</p>
              <h3 className="font-display text-2xl mb-3">{p.t}</h3>
              <p className="text-sm text-exodus-muted leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-exodus-surface border-y border-exodus-border py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <ChartLineUp size={48} weight="thin" className="mx-auto text-exodus-gold mb-8" />
          <p className="font-display text-3xl lg:text-4xl leading-snug">
            “Most people don't need to be sold — they need to be taught. When you understand the math, the right decision becomes obvious.”
          </p>
          <p className="eyebrow mt-8">— Leon · The Structured Advisor</p>
        </div>
      </section>

      {/* FINAL CTA — soft */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
        <p className="eyebrow"><span className="gold-divider" />Begin</p>
        <h2 className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
          When you're ready to <span className="gold-text italic">understand</span>,<br />we're here.
        </h2>
        <p className="text-exodus-muted mt-8 max-w-xl mx-auto leading-relaxed">
          Start with the library. Reserve a session when the questions get specific. There is no rush, and there is no pitch.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-10">
          <Link to="/learn" className="btn-gold" data-testid="final-cta-library">Browse the Library</Link>
          <Link to="/reserve" className="btn-ghost" data-testid="final-cta-reserve">Reserve a Session</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
